import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { useAuthStore } from '../stores/useAuthStore';
import { Logo } from '../components/icons/Logo';
import { EyeIcon, EyeOffIcon } from '../components/icons/Icons';
import toast from 'react-hot-toast';
import { MAINTENANCE_MODE, MAINTENANCE_MESSAGE, isEmailAllowed } from '../config/maintenance';
import { Input, Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { INVITATION_TOKEN_KEY } from '../constants/storageKeys';
import { resolvePostAuthPath } from '../utils/postAuthRedirect';

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    const [loginError, setLoginError] = useState<string>('');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const login = useAuthStore((state) => state.login);
    const prefersReducedMotion = useReducedMotion();

    // Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
    // --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
    const headingVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
    };
    const cardVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
    };

    // Handle OAuth callback
    useEffect(() => {
        const token = searchParams.get('token');
        const error = searchParams.get('error');
        const provider = searchParams.get('provider');

        console.log('[LoginPage] OAuth callback check:', {
            hasToken: !!token,
            tokenLength: token?.length,
            tokenPreview: token ? `${token.substring(0, 20)}...` : null,
            error,
            provider
        });

        if (token) {
            setIsLoading(true);
            AuthService.handleSocialCallback(token)
                .then(async (response) => {
                    console.log('[LoginPage] Social login success:', response.user?.email);
                    login(response.user);
                    const providerName = provider === 'google' ? 'Google' : 'GitHub';
                    toast.success(`Login com ${providerName} realizado com sucesso!`);

                    // Check for invitation token
                    const inviteToken = localStorage.getItem(INVITATION_TOKEN_KEY);
                    if (inviteToken) {
                        try {
                            await AuthService.acceptInviteToken(inviteToken);
                            toast.success('Você entrou na empresa com sucesso!');
                            localStorage.removeItem(INVITATION_TOKEN_KEY);
                        } catch (e) {
                            console.error('Failed to auto-accept invite:', e);
                        }
                    }

                    navigate(resolvePostAuthPath(response.user));
                })
                .catch((err) => {
                    console.error('[LoginPage] Social callback error:', err);
                    console.error('[LoginPage] Error details:', err.response?.data || err.message);
                    toast.error('Erro ao processar login social. Tente novamente.');
                    // Clean URL params
                    navigate('/login', { replace: true });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else if (error) {
            const providerName = provider === 'google' ? 'Google' : provider === 'github' ? 'GitHub' : 'social';
            toast.error(`Falha na autenticação com ${providerName}. Tente novamente.`);
            // Clean URL params
            navigate('/login', { replace: true });
        }
    }, [searchParams, login, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationErrors({}); // Limpar erros anteriores
        setLoginError(''); // Limpar erro de login anterior

        // Check maintenance mode before attempting login
        if (MAINTENANCE_MODE && !isEmailAllowed(email)) {
            setLoginError(MAINTENANCE_MESSAGE);
            toast.error(MAINTENANCE_MESSAGE);
            return;
        }

        setIsLoading(true);
        try {
            const response = await AuthService.login({ email, password });
            login(response.user);
            toast.success('Login realizado com sucesso!');
            navigate(resolvePostAuthPath(response.user));
        } catch (error: any) {
            console.error('[LoginPage] Login error:', error);
            console.error('[LoginPage] Error message:', error.message);
            console.error('[LoginPage] Validation errors:', error.validationErrors);

            // Capturar erros de validação (AuthService adiciona validationErrors)
            if (error.validationErrors) {
                setValidationErrors(error.validationErrors);
                toast.error('Por favor, corrija os erros no formulário.');
            } else {
                // Sempre mostrar uma mensagem de erro
                const errorMessage = error.message || 'Email ou senha inválidos. Tente novamente.';
                console.log('[LoginPage] Showing toast:', errorMessage);
                setLoginError(errorMessage); // Mostrar erro visualmente
                toast.error(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-snapfy-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            {/* Maintenance Mode Banner */}
            {MAINTENANCE_MODE && (
                <div className="fixed top-0 left-0 right-0 bg-amber-500/90 backdrop-blur-sm border-b border-amber-600 z-50">
                    <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-center gap-3">
                            <svg className="h-5 w-5 text-amber-900" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-amber-900 font-semibold text-sm">
                                🚧 Sistema em manutenção - Acesso restrito apenas para administradores
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <motion.div
                className="sm:mx-auto sm:w-full sm:max-w-md"
                variants={headingVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-center">
                    <Logo className="h-12 w-auto" />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-snapfy-text-primary">
                    Entre na sua conta
                </h2>
                <p className="mt-2 text-center text-sm text-snapfy-text-secondary">
                    Ou{' '}
                    <Link to="/register" className="font-medium text-snapfy-primary hover:text-snapfy-accent-soft">
                        crie uma nova conta gratuitamente
                    </Link>
                </p>
            </motion.div>

            <motion.div
                className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="card bg-snapfy-surface py-8 px-4 shadow-card rounded-card sm:px-10 border border-snapfy-border">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            label="Endereço de Email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={validationErrors.email?.[0]}
                        />

                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            label="Senha"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={validationErrors.password?.[0]}
                            trailingAffordance={
                                <button
                                    type="button"
                                    className="flex items-center text-snapfy-text-secondary hover:text-snapfy-text-primary"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            }
                        />

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 accent-snapfy-primary focus:ring-snapfy-focus border-snapfy-border rounded bg-snapfy-surface-hover"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-snapfy-text-secondary">
                                    Lembrar-me
                                </label>
                            </div>

                            <div className="text-sm">
                                <Link to="/forgot-password" className="font-medium text-snapfy-primary hover:text-snapfy-accent-soft">
                                    Esqueceu sua senha?
                                </Link>
                            </div>
                        </div>

                        {loginError && (
                            <div className="rounded-md bg-snapfy-error/10 border border-snapfy-error/50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-snapfy-error" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-snapfy-error font-medium">{loginError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <Button
                                type="submit"
                                variant="solid"
                                className="w-full"
                                isLoading={isLoading}
                                disabled={isLoading}
                            >
                                Entrar
                            </Button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-snapfy-border" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-snapfy-surface text-snapfy-text-secondary">
                                    Ou continue com
                                </span>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <div>
                                <a
                                    href={AuthService.getSocialLoginUrl('google')}
                                    className="w-full inline-flex justify-center py-2 px-4 border border-snapfy-border rounded-md shadow-sm bg-snapfy-surface-hover text-sm font-medium text-snapfy-text-secondary hover:bg-snapfy-inner transition-colors"
                                >
                                    <span className="sr-only">Entrar com Google</span>
                                    <svg className="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                                    </svg>
                                </a>
                            </div>

                            <div>
                                <a
                                    href={AuthService.getSocialLoginUrl('github')}
                                    className="w-full inline-flex justify-center py-2 px-4 border border-snapfy-border rounded-md shadow-sm bg-snapfy-surface-hover text-sm font-medium text-snapfy-text-secondary hover:bg-snapfy-inner transition-colors"
                                >
                                    <span className="sr-only">Entrar com GitHub</span>
                                    <svg className="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default LoginPage;
